import { useState, useEffect, useMemo } from "react";
import { useModelStore } from "@/store/useModelStore";
import { useSupportStore } from "@/store/useSupportStore";
import { useTrussStore } from "@/hooks/useTrussManagement";
import { getDesignationDataForType } from "@/data/sectionDesignations";
import { useBeamTrussStore } from "@/hooks/useBeamTrussManagement";
import { useLoadcaseGroupStore } from "@/store/useLoadcaseGroupStore";

import type { SectionType } from "@/types/property";
import { MaterialType } from "@/types/property";

import { usePropertyStore } from "@/store/usePropertyStore";
import { useBeamSectionStore } from "@/hooks/useBeamSection";
import type {
  Node as StoreNode,
  Beam as StoreBeam,
} from "@/types/nnstructural2D";
import {
  type ValidationError,
  type ValidationResult,
  validateCompleteModel,
} from "@/utils/structuralValidation";

import type { BeamTrussType } from "@/types/beamTruss";

interface ParsedNode {
  id: number;
  x: number;
  y: number;
  z: number;
}
interface ParsedBeam {
  id: number;
  startNodeId: number;
  endNodeId: number;
  type: "beam";
}

export const TerminalDialog = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const nodes = useModelStore((state) => state.nodes);
  const beams = useModelStore((state) => state.beams);
  const setNodes = useModelStore((state) => state.setNodes);
  const setBeams = useModelStore((state) => state.setBeams);
  const supports = useSupportStore((state) => state.supports);
  const addSupport = useSupportStore((state) => state.addSupport);
  const properties = usePropertyStore((state) => state.properties);
  const beamTrusses = useBeamTrussStore((s) => s.beamTrusses);
  const nodeTrussesArray = useTrussStore((s) => s.nodeTrusses);
  const loadcaseGroups = useLoadcaseGroupStore((s) => s.loadcaseGroups);

  const clearAllSupports = useSupportStore((state) => state.clearAllSupports);

  const [cmdText, setCmdText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const hiddenStyle = !isOpen ? { display: "none" } : {};

  const SUPPORT_POSITION_AXIS: Record<string, string> = {
    below: "-Z",
    above: "+Z",
    left: "-X",
    right: "+X",
    front: "+Y",
    back: "-Y",
  };

  const AXIS_TO_SUPPORT_POSITION: Record<string, string> = {
    "-Z": "below",
    "+Z": "above",
    "-X": "left",
    "+X": "right",
    "+Y": "front",
    "-Y": "back",
  };

  const VALID_SUPPORT_TYPES = ["pinned", "roller", "fixed"];

  const VALID_BEAM_LOADCASE_TYPES = [
    "DISTRIBUTED1",
    "DISTRIBUTED2",
    "CONCENTRATED",
    "TWISTED",
  ];

  const VALID_NODE_LOADCASE_KEYS = [
    "RX_X",
    "RX_Y",
    "RX_Z",
    "FX_X",
    "FX_Y",
    "FX_Z",
  ];

  const SECTION_TYPES: SectionType[] = [
    "Bulb Angle",
    "Channel",
    "Custom Section",
    "Equal Angle",
    "I-Beam",
    "Tee Bar",
    "Unequal Angle",
  ];

  const formatBeamId = (id: string | number) => Number(id).toString();
  const formatBeamNode = (nodeId: string | number) =>
    Number(nodeId).toString().padStart(3);
  const formatBeam = (b: StoreBeam) =>
    `${formatBeamId(b.id)}${formatBeamNode(b.startNodeId)}${formatBeamNode(b.endNodeId)};`;

  const getSupportNodeIds = (s: any): string[] => {
    if (Array.isArray(s.nodeIds)) return s.nodeIds.map(String);
    if (Array.isArray(s.nodes)) return s.nodes.map(String);
    if (s.nodeId != null) return [String(s.nodeId)];
    return [];
  };

  const formatSupportNode = (s: any) => {
    const ids = getSupportNodeIds(s);
    if (ids.length === 0) return "";
    return ids.join(",");
  };

  const formatSupport = (s: any, idx: number) => {
    const ids = formatSupportNode(s); // "" or "1,2"
    const axis = SUPPORT_POSITION_AXIS[s.position] ?? s.position;
    const base = `${idx + 1} ${axis} ${s.type?.toUpperCase()}`;

    // ---- FIXED SUPPORT ----
    if (s.type === "fixed" && s.dof) {
      const { Fx, Fy, Fz, Rx, Ry, Rz } = s.dof;

      // DOF always present, NODEID optional
      return ids
        ? `${base} DOF(${Fx},${Fy},${Fz},${Rx},${Ry},${Rz}) ${ids};`
        : `${base} DOF(${Fx},${Fy},${Fz},${Rx},${Ry},${Rz}) ;`;
    }

    // ---- PINNED / OTHERS ----
    return ids ? `${base} ${ids};` : `${base} ;`;
  };

  // Example line format: P001 1,2,3 steel I-Beam;
  const formatProperty = (p: any) => {
    const pid = (p.P_ID || "").toString();
    const material = (p.material || "None").padEnd(6);
    const sectionType = p.sectionType || "";
    const designation = p.sectionProperties?.designation
      ? ` ${p.sectionProperties.designation}`
      : "";
    const beamIds = p.beamIds?.length ? ` ${p.beamIds.join(",")}` : "";

    return `${pid} ${material} ${sectionType}${designation}${beamIds};`;
  };

  // Format nodes: 5 items per row
  const formatNodesInRows = (nodes: StoreNode[]) => {
    const lines: string[] = [];
    let row: string[] = [];

    nodes.forEach((n, idx) => {
      row.push(
        `${n.id} ${n.x.toFixed(2)} ${n.y.toFixed(2)} ${n.z.toFixed(2)};`
      );

      if (row.length === 3 || idx === nodes.length - 1) {
        lines.push(row.join(" "));
        row = [];
      }
    });

    return lines.join("\n");
  };

  const formatBeamsInRows = (beams: StoreBeam[]) => {
    const lines: string[] = [];
    let row: string[] = [];

    beams.forEach((b, idx) => {
      row.push(formatBeam(b));

      if (row.length === 4 || idx === beams.length - 1) {
        lines.push(row.join(" "));
        row = [];
      }
    });

    return lines.join("\n");
  };

  const formatSupportsInRows = (supports: any[]) => {
    const lines: string[] = [];
    let row: string[] = [];

    supports.forEach((s, idx) => {
      row.push(formatSupport(s, idx));

      if (row.length === 2 || idx === supports.length - 1) {
        lines.push(row.join(" "));
        row = [];
      }
    });

    return lines.join("\n");
  };
  const formatPropertiesInRows = (props: any[]) => {
    const lines: string[] = [];
    props.forEach((p) => {
      lines.push(formatProperty(p));
    });
    return lines.join("\n");
  };

  const formatLoadcaseGroups = () => {
    const lines: string[] = [];

    loadcaseGroups.forEach((group) => {
      // Get beam loads for this group
      const groupBeamLoads = beamTrusses.filter((bt) =>
        group.beamLoadIds.includes(bt.id)
      );

      // Get node loads for this group
      const groupNodeLoads = groupedNodeTrusses.filter((nt: any) =>
        group.nodeLoadIds.includes(nt.id)
      );

      // Start loadcase block
      lines.push(`${group.name} Start`);

      // Beam loads section
      lines.push(`BEAM LOADCASE START (FORMAT: ID TYPE VALUES BEAMIDS)`);

      groupBeamLoads.forEach((t, idx) => {
        let type = "";
        let values: string[] = [];

        switch (t.type) {
          case "distributed_uniform":
            type = "DISTRIBUTED1";
            values.push(
              `DX:${t.dx || 0}`,
              `DY:${t.dy || 0}`,
              `DZ:${t.dz || 0}`
            );
            break;
          case "distributed_variable":
            type = "DISTRIBUTED2";
            values.push(
              `DX_L:${t.dxLeft || 0}`,
              `DY_L:${t.dyLeft || 0}`,
              `DZ_L:${t.dzLeft || 0}`,
              `DX_R:${t.dxRight || 0}`,
              `DY_R:${t.dyRight || 0}`,
              `DZ_R:${t.dzRight || 0}`
            );
            break;
          case "concentrated_load":
            type = "CONCENTRATED";
            let refPoint = "S_Node";
            if (t.percentageFromNode === 50) {
              refPoint = "Center";
            } else if (t.referenceNode === "node2") {
              refPoint = "E_Node";
            }
            values.push(
              `FX:${t.fx || 0}`,
              `FY:${t.fy || 0}`,
              `FZ:${t.fz || 0}`,
              `REF:${refPoint}`,
              `DIST:${t.percentageFromNode || 0}`
            );
            break;
          case "twisted_load":
            type = "TWISTED";
            values.push(
              `TORQUE:${t.torque}`,
              `DIR:${t.torqueDirection?.toUpperCase()}`
            );
            break;
        }

        const beamIds = t.beamIds.length > 0 ? t.beamIds.join(",") : "";
        lines.push(`${idx + 1} ${type} ${values.join(" ")} ${beamIds};`);
      });

      lines.push(`BEAM LOADCASE END`);
      lines.push(``);
      // Node loads section
      lines.push(`NODE LOADCASE START (FORMAT: ID VALUES NODEIDS)`);

      groupNodeLoads.forEach((t: any, idx: number) => {
        const values: string[] = [];

        if (t.type === "regular") {
          values.push(
            `RX_X:${t.rotationValues.X}`,
            `RX_Y:${t.rotationValues.Y}`,
            `RX_Z:${t.rotationValues.Z}`
          );
        } else {
          values.push(
            `FX_X:${t.perpendicularValues.X}`,
            `FX_Y:${t.perpendicularValues.Y}`,
            `FX_Z:${t.perpendicularValues.Z}`
          );
        }

        const nodeIds = t.nodeIds.length ? t.nodeIds.join(",") : "";
        lines.push(`${idx + 1} ${values.join(" ")} ${nodeIds};`);
      });

      lines.push(`NODE LOADCASE END`);

      // End loadcase block
      lines.push(`${group.name} END`);
      lines.push(``); // Empty line between loadcases
    });

    return lines.join("\n");
  };

  const groupedNodeTrusses = useMemo(() => {
    const grouped = new Map<string, any>();

    nodeTrussesArray.forEach((t) => {
      const key = t.presetName ?? t.label;

      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          name: key,
          type: t.type,
          nodeIds: [...t.nodeIds],
          rotationValues: { X: 0, Y: 0, Z: 0 },
          perpendicularValues: { X: 0, Y: 0, Z: 0 },
          visible: t.visible,
        });
      }

      const g = grouped.get(key);

      if (t.type === "regular") {
        g.rotationValues[t.plane] = t.rotationValue ?? 0;
      } else {
        g.perpendicularValues[t.plane] = t.rotationValue ?? 0;
      }

      t.nodeIds.forEach((id: string) => {
        if (!g.nodeIds.includes(id)) g.nodeIds.push(id);
      });
    });

    return Array.from(grouped.values());
  }, [nodeTrussesArray]);

  useEffect(() => {
    if (!isOpen) return;
    if (isDirty) return;

    const nodeLines = formatNodesInRows(nodes);
    const beamLines = formatBeamsInRows(beams);
    const supportLines = formatSupportsInRows(supports);
    const propertyLines = formatPropertiesInRows(properties);
    const loadcaseGroupLines = formatLoadcaseGroups();

    const template = `NODES START(FORMAT: NODEID X Y Z)
${nodeLines}
NODES END

BEAMS START(FORMAT: BEAMID STARTNODEID ENDNODEID)
${beamLines}
BEAMS END

SUPPORTS START(FORMAT: ID POSITION TYPE DOF(Fx,Fy,Fz,Rx,Ry,Rz) NODEID)
${supportLines}
SUPPORTS END

PROPERTIES START(FORMAT: ID MATERIAL SECTIONTYPE DESIGNATION BEAMIDS)
${propertyLines}
PROPERTIES END

${loadcaseGroupLines}`;

    setCmdText(template);
  }, [
    isOpen,
    nodes,
    beams,
    isDirty,
    supports,
    properties,
    beamTrusses,
    nodeTrussesArray,
    loadcaseGroups,
  ]);

  useEffect(() => {
    if (isOpen) {
      setIsDirty(false);
      setErrorMessage("");
    }
  }, [isOpen]);

  // --------------------------------------------------------------

  const parseCMD = (text: string) => {
    const lines = text.split("\n").map((l) => l.trim());

    let nodeSection = false;
    let beamSection = false;
    let supportSection = false;
    let propertySection = false;
    let nodeLoadcaseSection = false;
    let beamLoadcaseSection = false;

    // Track current loadcase group for nested parsing
    let currentLoadcaseGroupName: string | null = null;
    let currentLoadcaseBeamLoads: any[] = [];
    let currentLoadcaseNodeLoads: any[] = [];

    const parsedNodes: ParsedNode[] = [];
    const parsedBeams: ParsedBeam[] = [];
    const parsedSupports: any[] = [];
    const parsedProperties: any[] = [];
    const parsedNodeLoadcases: any[] = [];
    const parsedBeamLoadcases: any[] = [];
    const parsedLoadcaseGroups: any[] = [];

    const errors: string[] = [];

    for (const line of lines) {
      if (!line || line.startsWith("CMD")) continue;

      // === Detect sections ===
      if (line.includes("NODES START")) {
        nodeSection = true;
        beamSection = false;
        supportSection = false;
        continue;
      }
      if (line.includes("NODES END")) {
        nodeSection = false;
        continue;
      }

      if (line.includes("BEAMS START")) {
        beamSection = true;
        nodeSection = false;
        supportSection = false;
        continue;
      }
      if (line.includes("BEAMS END")) {
        beamSection = false;
        continue;
      }

      if (line.includes("SUPPORTS START")) {
        supportSection = true;
        nodeSection = false;
        beamSection = false;
        continue;
      }
      if (line.includes("SUPPORTS END")) {
        supportSection = false;
        continue;
      }
      if (line.includes("PROPERTIES START")) {
        propertySection = true;
        nodeSection = beamSection = supportSection = false;
        continue;
      }
      if (line.includes("PROPERTIES END")) {
        propertySection = false;
        continue;
      }

      // === NESTED LOADCASE GROUP DETECTION ===
      // Check for "LoadcaseName Start" pattern (but not "BEAM LOADCASE START" etc.)
      if (
        line.endsWith(" Start") &&
        !line.includes("LOADCASE START") &&
        !line.includes("NODES START") &&
        !line.includes("BEAMS START") &&
        !line.includes("SUPPORTS START") &&
        !line.includes("PROPERTIES START")
      ) {
        currentLoadcaseGroupName = line.replace(" Start", "");
        currentLoadcaseBeamLoads = [];
        currentLoadcaseNodeLoads = [];
        // Reset section flags when entering a new loadcase group
        beamLoadcaseSection = false;
        nodeLoadcaseSection = false;
        continue;
      }

      // Check for "LoadcaseName END" pattern (but not "BEAM LOADCASE END" etc.)
      if (
        line.endsWith(" END") &&
        !line.includes("LOADCASE END") &&
        !line.includes("NODES END") &&
        !line.includes("BEAMS END") &&
        !line.includes("SUPPORTS END") &&
        !line.includes("PROPERTIES END")
      ) {
        const groupName = line.replace(" END", "");
        if (currentLoadcaseGroupName === groupName) {
          // Generate unique IDs for the loads before adding to main arrays
          const beamLoadIdMap = new Map<string, string>();
          const nodeLoadIdMap = new Map<string, string>();

          currentLoadcaseBeamLoads.forEach((bl, idx) => {
            const uniqueId = `beam-load-${parsedBeamLoadcases.length + idx + 1}`;
            beamLoadIdMap.set(bl.id, uniqueId);
            bl.id = uniqueId;
          });

          currentLoadcaseNodeLoads.forEach((nl, idx) => {
            const uniqueId = `node-load-${parsedNodeLoadcases.length + idx + 1}`;
            nodeLoadIdMap.set(nl.id, uniqueId);
            nl.id = uniqueId;
          });

          // Finalize the loadcase group with unique IDs
          parsedLoadcaseGroups.push({
            id: `loadcase-${parsedLoadcaseGroups.length + 1}`,
            name: currentLoadcaseGroupName,
            beamLoadIds: currentLoadcaseBeamLoads.map((bl) => bl.id),
            nodeLoadIds: currentLoadcaseNodeLoads.map((nl) => nl.id),
          });

          // Add the loads to the main arrays
          currentLoadcaseBeamLoads.forEach((bl) =>
            parsedBeamLoadcases.push(bl)
          );
          currentLoadcaseNodeLoads.forEach((nl) =>
            parsedNodeLoadcases.push(nl)
          );

          currentLoadcaseGroupName = null;
          currentLoadcaseBeamLoads = [];
          currentLoadcaseNodeLoads = [];
          // Reset section flags when exiting a loadcase group
          beamLoadcaseSection = false;
          nodeLoadcaseSection = false;
        }
        continue;
      }

      if (line.includes("NODE LOADCASE START")) {
        nodeLoadcaseSection = true;
        continue;
      }
      if (line.includes("NODE LOADCASE END")) {
        nodeLoadcaseSection = false;
        continue;
      }

      if (line.includes("BEAM LOADCASE START")) {
        beamLoadcaseSection = true;
        continue;
      }
      if (line.includes("BEAM LOADCASE END")) {
        beamLoadcaseSection = false;
        continue;
      }

      // ===================== NODES ====================
      if (nodeSection) {
        const parts = line
          .split(";")
          .map((p) => p.trim())
          .filter(Boolean);

        parts.forEach((p) => {
          const vals = p.split(/\s+/);
          if (vals.length < 3 || vals.length > 4) {
            errors.push(`Node format error: "${p}"`);
            return;
          }
          const offset = vals.length === 4 ? 1 : 0;
          parsedNodes.push({
            id: parsedNodes.length + 1, // AUTO-ID
            x: Number(vals[offset]),
            y: Number(vals[offset + 1]),
            z: Number(vals[offset + 2]),
          });
        });

        continue;
      }

      // ===================== BEAMS ====================
      if (beamSection) {
        const parts = line
          .split(";")
          .map((p) => p.trim())
          .filter(Boolean);

        parts.forEach((p) => {
          const vals = p.split(/\s+/);
          if (vals.length < 3 || vals.length > 4) {
            errors.push(`Beam format error: "${p}"`);
            return;
          }

          const offset = vals.length === 3 ? 1 : 0;

          parsedBeams.push({
            id: parsedBeams.length + 1,
            startNodeId: Number(vals[offset]),
            endNodeId: Number(vals[offset + 1]),
            type: "beam",
          });
        });

        continue;
      }

      if (supportSection) {
        const parts = line
          .split(";")
          .map((p) => p.trim())
          .filter(Boolean);

        parts.forEach((p) => {
          const dofMatch = p.match(/DOF\((.*?)\)/i);
          const dofValues = dofMatch
            ? dofMatch[1].split(",").map(Number)
            : null;

          let tokens = p
            .replace(/DOF\((.*?)\)/i, "")
            .trim()
            .split(/\s+/);

          const hasId = /^\d+$/.test(tokens[0]);
          const base = hasId ? 1 : 0;

          const rawPosition = tokens[base].toUpperCase();
          const position =
            AXIS_TO_SUPPORT_POSITION[rawPosition] ?? rawPosition.toLowerCase();

          const type = tokens[base + 1]?.toLowerCase();

          // Validate position
          if (!AXIS_TO_SUPPORT_POSITION[rawPosition]) {
            errors.push(
              `Invalid support position: "${tokens[base]}" (must be +X, -X, +Y, -Y, +Z, or -Z)`
            );
            return;
          }

          // Validate support type
          if (!type) {
            errors.push(`Missing support type in: "${p}"`);
            return;
          }

          if (!VALID_SUPPORT_TYPES.includes(type)) {
            errors.push(
              `Invalid support type: "${tokens[base + 1]}" (must be PINNED, ROLLER or FIXED)`
            );
            return;
          }

          let nodeIds: number[] = [];
          const last = tokens[tokens.length - 1];
          if (/^[\d,]+$/.test(last)) {
            nodeIds = last.split(",").map(Number);
          }

          const supportObj: any = {
            nodeIds,
            position, // ✅ mapped correctly
            type: type.toLowerCase(),
          };

          if (type.toLowerCase() === "fixed" && dofValues) {
            if (dofValues.length !== 6) {
              errors.push(`Invalid DOF values: "${p}"`);
              return;
            }

            supportObj.dof = {
              Fx: dofValues[0],
              Fy: dofValues[1],
              Fz: dofValues[2],
              Rx: dofValues[3],
              Ry: dofValues[4],
              Rz: dofValues[5],
            };
          }

          parsedSupports.push(supportObj);
        });

        continue;
      }

      // ===================== PROPERTIES ====================
      if (propertySection) {
        const parts = line
          .split(";")
          .map((p) => p.trim())
          .filter(Boolean);

        parts.forEach((p) => {
          const tokens = p.split(/\s+/);

          console.log("token data", tokens);

          if (tokens.length < 4) {
            errors.push(`Property format invalid: "${p}"`);
            return;
          }

          let cursor = 0;

          // 1️⃣ Property ID
          const P_ID = tokens[cursor++];

          // 2️⃣ Material
          const material: MaterialType =
            tokens[cursor] === "Steel" ? "Steel" : "None";
          cursor++;

          // 3️⃣ Detect SECTION TYPE dynamically
          let sectionType: SectionType | undefined;
          let sectionTypeTokenCount = 0;

          for (const type of SECTION_TYPES) {
            const typeTokens = type.split(" ");
            const candidate = tokens
              .slice(cursor, cursor + typeTokens.length)
              .join(" ");

            if (candidate === type) {
              sectionType = type;
              sectionTypeTokenCount = typeTokens.length;
              break;
            }
          }

          if (!sectionType) {
            errors.push(`Unknown section type in "${p}"`);
            return;
          }

          cursor += sectionTypeTokenCount;

          let beamIds: string[] = [];
          let designationTokens = tokens.slice(cursor);

          let sectionData = null;

          // 🔁 Try longest designation first, then shrink
          while (designationTokens.length > 0) {
            const designationAttempt = designationTokens.join(" ");

            sectionData = getDesignationDataForType(
              sectionType,
              designationAttempt
            );

            if (sectionData) {
              break; // ✅ Found valid designation
            }

            const token = designationTokens.pop()!;

            // split comma-separated IDs properly
            token.split(",").forEach((id) => {
              if (id.trim()) beamIds.unshift(id.trim());
            });
          }

          if (!sectionData) {
            errors.push(
              `Unknown section: ${sectionType} ${tokens.slice(cursor).join(" ")}`
            );
            return;
          }

          const designation = designationTokens.join(" ");

          if (!sectionData) {
            errors.push(`Unknown section: ${sectionType} ${designation}`);
            return;
          }

          parsedProperties.push({
            P_ID,
            beamIds,
            material,
            sectionType,
            sectionProperties: {
              ...sectionData,
              designation, // full designation like "ISBA 100"
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        });

        continue;
      }

      if (nodeLoadcaseSection) {
        let tokens = line.replace(";", "").split(/\s+/);

        const id = tokens.shift()!;

        // NODE IDS LAST
        let nodeIds: number[] = [];

        const last = tokens[tokens.length - 1];
        if (/^[\d,]+$/.test(last)) {
          nodeIds = last.split(",").map(Number);
          tokens.pop();
        }

        const values: Record<string, number> = {};
        const invalidKeys: string[] = [];

        for (const kv of tokens) {
          if (!kv.trim()) continue;
          const colonIndex = kv.indexOf(":");
          if (colonIndex === -1) continue;
          const k = kv.slice(0, colonIndex).trim();
          const v = kv.slice(colonIndex + 1).trim();
          if (!k || !v) continue;
          if (!VALID_NODE_LOADCASE_KEYS.includes(k)) {
            invalidKeys.push(k);
            continue;
          }

          values[k] = Number(v);
        }

        if (invalidKeys.length > 0) {
          errors.push(
            `Invalid node loadcase keys: "${invalidKeys.join(", ")}" (must be RX_X, RX_Y, RX_Z, FX_X, FX_Y, or FX_Z)`
          );
          continue;
        }

        if (Object.keys(values).length === 0) {
          errors.push(`Node loadcase ${id} has no valid load values`);
          continue;
        }

        const hasRegular = Object.keys(values).some((k) => k.startsWith("RX_"));
        const hasPerpendicular = Object.keys(values).some((k) =>
          k.startsWith("FX_")
        );

        if (hasRegular && hasPerpendicular) {
          errors.push(
            `Node loadcase ${id} cannot mix RX_ (rotation) and FX_ (force) values`
          );
          continue;
        }

        // Generate name based on type (regular or perpendicular)
        const autoName = hasRegular ? `NodeLoad_R${id}` : `NodeLoad_P${id}`;

        const nodeLoadEntry = {
          id,
          name: autoName,
          targetType: "NODE",
          targetIds: nodeIds,
          values,
        };

        // If inside a loadcase group, add to that group's loads
        if (currentLoadcaseGroupName) {
          currentLoadcaseNodeLoads.push(nodeLoadEntry);
        } else {
          parsedNodeLoadcases.push(nodeLoadEntry);
        }

        continue;
      }

      if (beamLoadcaseSection) {
        let tokens = line.replace(";", "").split(/\s+/);

        const id = tokens.shift()!;
        const type = tokens.shift()!;

        // Validate beam loadcase type
        if (!type) {
          errors.push(`Missing loadcase type for beam load ${id}`);
          continue;
        }

        if (!VALID_BEAM_LOADCASE_TYPES.includes(type.toUpperCase())) {
          errors.push(
            `Invalid beam loadcase type: "${type}" for load ${id} (must be DISTRIBUTED1, DISTRIBUTED2, CONCENTRATED, or TWISTED)`
          );
          continue;
        }

        // BEAM IDS LAST
        let beamIds: number[] = [];

        const last = tokens[tokens.length - 1];
        if (/^[\d,]+$/.test(last)) {
          beamIds = last.split(",").map(Number);
          tokens.pop();
        }

        const values: any = {};

        tokens.forEach((token) => {
          if (token.includes(":")) {
            const [k, v] = token.split(":");
            values[k] = isNaN(Number(v)) ? v : Number(v);
          }
        });
        const typeUpper = type.toUpperCase();

        if (typeUpper === "DISTRIBUTED1") {
          const requiredKeys = ["DX", "DY", "DZ"];
          const missingKeys = requiredKeys.filter((k) => !(k in values));
          if (missingKeys.length > 0) {
            errors.push(
              `DISTRIBUTED1 loadcase ${id} missing required keys: ${missingKeys.join(", ")}`
            );
            continue;
          }
        }

        if (typeUpper === "DISTRIBUTED2") {
          const requiredKeys = ["DX_L", "DY_L", "DZ_L", "DX_R", "DY_R", "DZ_R"];
          const missingKeys = requiredKeys.filter((k) => !(k in values));
          if (missingKeys.length > 0) {
            errors.push(
              `DISTRIBUTED2 loadcase ${id} missing required keys: ${missingKeys.join(", ")}`
            );
            continue;
          }
        }

        if (typeUpper === "CONCENTRATED") {
          const requiredKeys = ["FX", "FY", "FZ", "REF", "DIST"];
          const missingKeys = requiredKeys.filter((k) => !(k in values));
          if (missingKeys.length > 0) {
            errors.push(
              `CONCENTRATED loadcase ${id} missing required keys: ${missingKeys.join(", ")}`
            );
            continue;
          }

          // Validate REF value
          if (
            values.REF !== "S_Node" &&
            values.REF !== "E_Node" &&
            values.REF !== "Center"
          ) {
            errors.push(
              `CONCENTRATED loadcase ${id} has invalid REF: "${values.REF}" (must be S_Node, E_Node, or Center)`
            );
            continue;
          }
        }

        if (typeUpper === "TWISTED") {
          const requiredKeys = ["TORQUE", "DIR"];
          const missingKeys = requiredKeys.filter((k) => !(k in values));
          if (missingKeys.length > 0) {
            errors.push(
              `TWISTED loadcase ${id} missing required keys: ${missingKeys.join(", ")}`
            );
            continue;
          }

          // Validate direction
          const dir = String(values.DIR).toUpperCase();
          if (dir !== "CLOCKWISE" && dir !== "ANTICLOCKWISE") {
            errors.push(
              `TWISTED loadcase ${id} has invalid DIR: "${values.DIR}" (must be CLOCKWISE or ANTICLOCKWISE)`
            );
            continue;
          }
        }

        const beamLoadEntry = {
          id,
          type,
          targetType: "BEAM",
          targetIds: beamIds,
          values,
        };

        // If inside a loadcase group, add to that group's loads
        if (currentLoadcaseGroupName) {
          currentLoadcaseBeamLoads.push(beamLoadEntry);
        } else {
          parsedBeamLoadcases.push(beamLoadEntry);
        }

        continue;
      }
    }

    return {
      parsedNodes,
      parsedBeams,
      parsedSupports,
      parsedProperties,
      parsedNodeLoadcases,
      parsedBeamLoadcases,
      parsedLoadcaseGroups,
      errors,
    };
  };

  const handleExecute = () => {
    const {
      parsedNodes,
      parsedBeams,
      parsedSupports,
      parsedProperties,
      parsedNodeLoadcases,
      parsedBeamLoadcases,
      parsedLoadcaseGroups,
      errors,
    } = parseCMD(cmdText);

    if (errors.length > 0) {
      setErrorMessage(errors.join("\n"));
      return;
    }

    const validationResult = validateCompleteModel({
      nodes: parsedNodes,
      beams: parsedBeams,
      supports: parsedSupports,
      properties: parsedProperties,
      nodeLoadcases: parsedNodeLoadcases,
      beamLoadcases: parsedBeamLoadcases,
    });

    if (!validationResult.isValid) {
      setErrorMessage(validationResult.errors.map((e) => e.message).join("\n"));
      return;
    }

    try {
      // 🔐 Freeze updates and signal terminal execution mode
      usePropertyStore.setState({ freezeUpdates: true } as any);
      window.dispatchEvent(new CustomEvent("terminal-execution-start"));

      // 0️⃣ ALL CLEARS (ONLY AFTER VALIDATION)
      const beamSectionStore = useBeamSectionStore.getState();
      beamSectionStore.clearAllSections();

      usePropertyStore.setState({ properties: [] });

      const nodeTrussStore = useTrussStore.getState();
      const allNodeTrusses = [...nodeTrussStore.nodeTrusses];
      allNodeTrusses.forEach((truss) => nodeTrussStore.removeTruss(truss.id));

      useBeamTrussStore.setState({
        beamTrusses: [],
        selectedBeamTrussId: null,
      });
      clearAllSupports();

      // 1️⃣-5️⃣ Apply ALL data (unchanged from your code)...
      setNodes(parsedNodes);
      setBeams(parsedBeams);

      parsedSupports.forEach((s) => {
        const created = addSupport(s.nodeIds.map(String), s.position, s.type);
        if (s.dof)
          useSupportStore.getState().updateSupport(created.id, { dof: s.dof });
      });

      if (parsedProperties.length > 0) {
        const normalizedProps = parsedProperties.map((p) => ({
          ...p,
          beamIds: p.beamIds.map((id) => Number(id)),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));

        usePropertyStore.setState({ properties: normalizedProps });
        const propertyStore = usePropertyStore.getState();
        normalizedProps.forEach((prop) => {
          if (prop.P_ID && prop.beamIds.length > 0) {
            propertyStore.applyPropertyToBeams(prop.P_ID, prop.beamIds);
          }
        });
      }

      // Node loadcases (unchanged)...
      if (parsedNodeLoadcases.length > 0) {
        parsedNodeLoadcases.forEach((lc) => {
          if (lc.targetType !== "NODE") return;
          const trussStore = useTrussStore.getState();
          trussStore.removeTrussesByLabel(lc.name);
          const rotationValues = {
            X: lc.values.RX_X ?? 0,
            Y: lc.values.RX_Y ?? 0,
            Z: lc.values.RX_Z ?? 0,
          };
          const perpendicularValues = {
            X: lc.values.FX_X ?? 0,
            Y: lc.values.FX_Y ?? 0,
            Z: lc.values.FX_Z ?? 0,
          };
          const targetNodeIds = lc.targetIds.map(String);
          trussStore.setSelectedPresetName(lc.name);
          if (targetNodeIds.length === 0) {
            (["X", "Y", "Z"] as const).forEach((axis) => {
              if (rotationValues[axis]) {
                const trussId = trussStore.addTruss(
                  [],
                  "regular",
                  axis,
                  undefined,
                  undefined,
                  rotationValues[axis],
                  lc.name
                );
                if (trussId) trussStore.updateTruss(trussId, { visible: true });
              }
              if (perpendicularValues[axis]) {
                const trussId = trussStore.addTruss(
                  [],
                  "perpendicular",
                  axis,
                  undefined,
                  undefined,
                  perpendicularValues[axis],
                  lc.name
                );
                if (trussId) trussStore.updateTruss(trussId, { visible: true });
              }
            });
            trussStore.setSelectedPresetName(null);
            return;
          }
          trussStore.applyLoadOnNode(
            targetNodeIds,
            rotationValues,
            perpendicularValues
          );
          trussStore.setSelectedPresetName(null);
        });
      }

      // Track mapping from parsed IDs to actual store IDs
      const parsedToActualBeamLoadId = new Map<string, string>();
      const parsedToActualNodeLoadId = new Map<string, string>();

      // Beam loadcases
      if (parsedBeamLoadcases.length > 0) {
        parsedBeamLoadcases
          .filter((lc) => lc.targetType === "BEAM")
          .forEach((lc) => {
            const store = useBeamTrussStore.getState();
            if (!lc.type)
              throw new Error(`Beam loadcase ${lc.id} missing TYPE`);
            const trussType =
              lc.type === "DISTRIBUTED1"
                ? "distributed_uniform"
                : lc.type === "DISTRIBUTED2"
                  ? "distributed_variable"
                  : lc.type === "CONCENTRATED"
                    ? "concentrated_load"
                    : lc.type === "TWISTED"
                      ? "twisted_load"
                      : null;
            if (!trussType)
              throw new Error(`Invalid beam loadcase type: ${lc.type}`);

            const payload = {
              type: trussType as BeamTrussType,
              dx: lc.values.DX,
              dy: lc.values.DY,
              dz: lc.values.DZ,
              dxLeft: lc.values.DX_L,
              dyLeft: lc.values.DY_L,
              dzLeft: lc.values.DZ_L,
              dxRight: lc.values.DX_R,
              dyRight: lc.values.DY_R,
              dzRight: lc.values.DZ_R,
              fx: lc.values.FX,
              fy: lc.values.FY,
              fz: lc.values.FZ,
              referenceNode: (lc.values.REF === "Center"
                ? "node1"
                : lc.values.REF === "E_Node"
                  ? "node2"
                  : "node1") as "node1" | "node2",
              percentageFromNode:
                lc.values.REF === "Center" ? 50 : lc.values.DIST,
              torque: lc.values.TORQUE,
              torqueDirection: lc.values.DIR?.toLowerCase() as
                | "clockwise"
                | "anticlockwise"
                | undefined,
            };

            const existing = store.beamTrusses.find((t) => t.label === lc.id);
            if (existing) {
              store.updateBeamTruss(existing.id, {
                ...payload,
                beamIds: lc.targetIds.map(String),
                visible: true,
              });
              // Track the mapping
              parsedToActualBeamLoadId.set(lc.id, existing.id);
            } else {
              const newId = store.addBeamTruss(
                lc.targetIds.map(String),
                trussType,
                undefined,
                payload.dx,
                payload.dy,
                payload.dz,
                payload.dxRight,
                payload.dyRight,
                payload.dzRight,
                payload.fx,
                payload.fy,
                payload.fz,
                payload.referenceNode,
                payload.percentageFromNode,
                undefined,
                undefined,
                undefined,
                undefined,
                undefined, // no label/name
                payload.torque,
                payload.torqueDirection
              );
              store.applyLoadToBeams(newId, lc.targetIds.map(String));
              store.updateBeamTruss(newId, { visible: true });
              // Track the mapping
              parsedToActualBeamLoadId.set(lc.id, newId);
            }
          });
      }

      // Also track node loadcase mappings
      if (parsedNodeLoadcases.length > 0) {
        const nodeTrussStore = useTrussStore.getState();
        // After node loadcases are created, map by name
        parsedNodeLoadcases.forEach((lc) => {
          const matchingTruss = nodeTrussStore.nodeTrusses.find(
            (nt) => nt.presetName === lc.name || nt.label === lc.name
          );
          if (matchingTruss) {
            parsedToActualNodeLoadId.set(lc.id, matchingTruss.id);
          }
        });
      }

      // Loadcase groups - apply after beam/node loadcases
      // Always clear existing loadcase groups first (even if no new ones to create)
      const loadcaseGroupStore = useLoadcaseGroupStore.getState();

      // Get a copy of the group names to avoid modifying array while iterating
      const existingGroupNames = loadcaseGroupStore.loadcaseGroups.map(
        (g) => g.name
      );
      existingGroupNames.forEach((name) => {
        loadcaseGroupStore.deleteLoadcaseGroup(name);
      });

      if (parsedLoadcaseGroups.length > 0) {
        // Create loadcase groups and add loads using the parsed-to-actual ID mappings
        parsedLoadcaseGroups.forEach((lg) => {
          try {
            loadcaseGroupStore.createLoadcaseGroup(lg.name);

            // Add beam loads using the mapping from parsed ID to actual store ID
            lg.beamLoadIds.forEach((parsedBeamLoadId: string) => {
              const actualId = parsedToActualBeamLoadId.get(parsedBeamLoadId);
              if (actualId) {
                loadcaseGroupStore.addBeamLoadToGroup(lg.name, actualId);
              }
            });

            // Add node loads using the mapping from parsed ID to actual store ID
            lg.nodeLoadIds.forEach((parsedNodeLoadId: string) => {
              const actualId = parsedToActualNodeLoadId.get(parsedNodeLoadId);
              if (actualId) {
                loadcaseGroupStore.addNodeLoadToGroup(lg.name, actualId);
              }
            });
          } catch (e: any) {
            console.warn(
              `Failed to create loadcase group "${lg.name}":`,
              e.message
            );
          }
        });

        // Set first loadcase as active
        if (parsedLoadcaseGroups.length > 0) {
          loadcaseGroupStore.setActiveLoadcase(parsedLoadcaseGroups[0].name);
        }
      }

      // 🔓 Unfreeze & refresh, signal terminal execution end
      usePropertyStore.setState({ freezeUpdates: false } as any);
      window.dispatchEvent(new CustomEvent("terminal-execution-end"));
      window.dispatchEvent(new CustomEvent("loads-updated"));
      useBeamTrussStore.setState((state) => ({
        ...state,
        beamTrusses: [...state.beamTrusses],
      }));
      useTrussStore.setState((state) => ({
        ...state,
        nodeTrusses: [...state.nodeTrusses],
      }));
      onClose();
    } catch (error: any) {
      setErrorMessage(error.message || "Apply failed");
      usePropertyStore.setState({ freezeUpdates: false } as any);
      window.dispatchEvent(new CustomEvent("terminal-execution-end"));
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
      style={hiddenStyle}
    >
      {/* MAIN WINDOW */}
      <div
        className="bg-[#f4f4f4] w-[720px] h-[470px] border border-black shadow-2xl rounded-sm flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center px-3 py-2 bg-gray-200 border-b border-black">
          <span className="text-sm font-semibold text-gray-800">
            Command Input
          </span>

          <button
            onClick={onClose}
            className="absolute right-2 text-black hover:text-gray-800 font-bold"
          >
            ✕
          </button>
        </div>

        {/* TEXT AREA */}
        <div className="flex-1 p-3 flex flex-col">
          <textarea
            value={cmdText}
            onChange={(e) => {
              setCmdText(e.target.value);
              setIsDirty(true);
              setErrorMessage(""); // Clear error when user edits
            }}
            className="
                    flex-1
                    border border-gray-400 
                    bg-white 
                    text-black 
                    text-sm 
                    p-3 
                    resize-none 
                    focus:outline-none 
                    overflow-y-auto
                "
            style={{
              fontFamily: "Menlo, Consolas, 'Courier New', monospace",
              whiteSpace: "pre",
            }}
          />
        </div>

        {/* FOOTER BUTTONS */}
        <div className="flex flex-col gap-2 px-3 py-2 border-t border-gray-300 bg-gray-100">
          {/* ERROR MESSAGE DISPLAY */}
          {errorMessage && (
            <div className="px-4 py-2 bg-red-100 border border-red-400 rounded text-red-700 text-xs overflow-y-auto max-h-24">
              <strong>Validation Error:</strong>
              <pre className="mt-1 text-xs whitespace-pre-wrap break-words">
                {errorMessage}
              </pre>
            </div>
          )}

          {/* BUTTONS ROW */}
          <div className="flex justify-end gap-3">
            {/* Clear Button */}
            <button
              onClick={() => {
                setCmdText("");
                setErrorMessage("");
              }}
              className="
                px-4 py-2
                bg-gray-500 hover:bg-gray-400
                rounded 
                text-sm font-medium
                transition
              "
            >
              Clear
            </button>

            {/* Execute Button */}
            <button
              onClick={handleExecute}
              className="
                px-4 py-2 rounded transition bg-blue-500 text-white hover:bg-blue-600
              "
            >
              Execute →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
